import axios from "axios";
import { enqueue, processQueue } from "./offlineQueue";

// In-memory token store — secure (not DOM-accessible),
// works reliably across same-origin AND cross-origin deployments.
let _accessToken = null;
let _refreshToken = null;

export function setTokens(access, refresh) {
  _accessToken = access || null;
  _refreshToken = refresh || null;
}

export function clearTokens() {
  _accessToken = null;
  _refreshToken = null;
}

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  // No withCredentials — Bearer tokens handle auth cross-origin.
  // Same-origin cookies are sent automatically by the browser.
});

// Request interceptor — attach Bearer token from memory
api.interceptors.request.use((config) => {
  if (_accessToken) {
    config.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const config = error.config || {};
    const isNetworkError = !error.response && (error.code === "ERR_NETWORK" || error.message === "Network Error");
    const isMutating = ["post", "put", "patch"].includes((config.method || "").toLowerCase());
    const isAuthRoute = (config.url || "").includes("/auth/");
    const isSyncReplay = config._isOfflineSync;

    if (isNetworkError && isMutating && !isAuthRoute && !isSyncReplay) {
      try {
        await enqueue({
          url: config.url,
          method: config.method,
          headers: config.headers,
          data: config.data,
        });
        return {
          data: { _offline: true, message: "Saved offline — will sync when connected" },
          status: 200,
          config,
        };
      } catch (queueErr) {
        return Promise.reject(queueErr);
      }
    }

    // Token refresh on 401 (skip auth routes to avoid loops)
    if (error.response?.status === 401 && !config._retry && !isAuthRoute) {
      config._retry = true;
      try {
        const refreshResp = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`,
          _refreshToken ? { refresh_token: _refreshToken } : {}
        );
        if (refreshResp.data?.access_token) {
          _accessToken = refreshResp.data.access_token;
        }
        return api(config);
      } catch {
        clearTokens();
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auto-sync when coming back online
window.addEventListener("online", () => {
  setTimeout(() => {
    processQueue(api).then(({ processed }) => {
      if (processed > 0) {
        window.dispatchEvent(new CustomEvent("offlineSync", { detail: { processed } }));
      }
    });
  }, 2000);
});

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === "SYNC_COMPLETE") {
      window.dispatchEvent(new CustomEvent("offlineSync", { detail: { processed: 0 } }));
    }
  });
}

export default api;
